import {
  type AlertType,
  type NotificationCellState,
  type SelectorOption,
  type SelectorValue,
} from "@src/appV2/redesign/WorkplaceUsers/components/NotificationPreferencesDialog/types";
import { type IAlertSetting, type INotification } from "src/modules/myAccount/types";

export interface CellContext {
  alertType: AlertType;
  alertSetting?: IAlertSetting;
  notification: INotification;
  notificationKey: string | boolean;
  webPushAllNotifications: boolean;
  allDisabled: boolean;
  flagDisabled: boolean;
  fieldDisabled: boolean;
}

export function buildNotificationCellState(context: CellContext): NotificationCellState {
  const {
    alertType,
    alertSetting,
    notification,
    notificationKey,
    webPushAllNotifications,
    allDisabled,
    flagDisabled,
    fieldDisabled,
  } = context;

  if (flagDisabled || fieldDisabled) {
    return { kind: "blank" };
  }

  if (!alertSetting && alertType !== "web_push") {
    return { kind: "blank" };
  }

  if (alertType === "web_push" && notification.web_push !== "web_push") {
    return { kind: "uneditable", text: webPushAllNotifications ? "On" : "Off" };
  }

  if (!alertSetting?.userModifiableState && alertType !== "web_push") {
    return { kind: "uneditable", text: alertSetting?.enabled ? "On" : "Off" };
  }

  if (allDisabled) {
    return { kind: "uneditable", text: "Off" };
  }

  let value: SelectorValue = "off";
  if (alertType === "web_push") {
    if (webPushAllNotifications) {
      value = "on";
    }
  } else if (alertSetting?.batch) {
    value = "daily";
  } else if (alertSetting?.enabled) {
    value = "on";
  }

  const isWebPush = alertType === "web_push";
  const isFieldLocked = !(isWebPush || alertSetting?.userModifiableState);
  const supportsDaily = Boolean(alertSetting?.userModifiableBatch) && !isWebPush;

  if (!supportsDaily) {
    return {
      kind: "switch",
      alertType,
      isOn: value === "on",
      isDisabled: isFieldLocked,
      notificationKey,
    };
  }

  const options: SelectorOption[] = [
    { value: "on", label: "On", disabled: isFieldLocked },
    { value: "off", label: "Off" },
    { value: "daily", label: "Daily" },
  ];

  return {
    kind: "select",
    alertType,
    value,
    options,
    isDisabled: isFieldLocked,
    notificationKey,
  };
}
